import { useState } from "react";
import type { FileUploadConstraints } from "../types";

export const hasAcceptedFileMimeType = (
  file: File | DataTransferItem,
  acceptedMimeTypes: FileUploadConstraints["acceptedMimeTypes"]
) => !file.type || acceptedMimeTypes.has(file.type);

const hasAcceptedFileType = (
  file: File,
  {
    acceptedFileExtensions,
    acceptedMimeTypes,
  }: Pick<FileUploadConstraints, "acceptedFileExtensions" | "acceptedMimeTypes">
) => {
  const lowerCaseFileName = file.name.toLowerCase();
  const hasAcceptedExtension = acceptedFileExtensions.some((extension) =>
    lowerCaseFileName.endsWith(extension)
  );
  return (
    hasAcceptedExtension && hasAcceptedFileMimeType(file, acceptedMimeTypes)
  );
};

export enum ValidationErrorType {
  UnsupportedFileType = "UnsupportedFileType",
  FileTooLarge = "FileTooLarge",
}

export const useValidation = ({
  maxFileSizeBytes,
  acceptedFileExtensions,
  acceptedMimeTypes,
}: FileUploadConstraints) => {
  const [validationErrorType, setValidationErrorType] = useState<
    ValidationErrorType | undefined
  >(undefined);

  const validateFile = (file: File) => {
    if (
      !hasAcceptedFileType(file, { acceptedFileExtensions, acceptedMimeTypes })
    ) {
      return {
        valid: false,
        type: ValidationErrorType.UnsupportedFileType,
      };
    }

    if (file.size > maxFileSizeBytes) {
      return {
        valid: false,
        type: ValidationErrorType.FileTooLarge,
      };
    }

    return { valid: true, type: undefined };
  };

  const clearValidationError = () => setValidationErrorType(undefined);

  return {
    validateFile,
    validationErrorType,
    setValidationErrorType,
    clearValidationError,
  };
};
